<template>
  <div class="mx-3 md:mx-0 bg-white py-4 rounded-lg mt-6 mb-20">
    <!-- Header -->
    <div
      class="flex flex-col md:gap-y-2 lg:flex-row lg:justify-between items-center pb-4 px-5 border-b border-b-gray-300 mb-4">
      <h3 class="text-xl text-blue font-semibold">{{ $t('leaderboard') }}</h3>
      <p class="text-sm text-gray-900 font-medium">{{ $t('leaderboard_info') }} (GTM+0)</p>
    </div>
    <!-- Your Rank -->
    <div v-if="userIsContestant" class="hidden lg:block border border-gray-300 rounded-lg bg-[#20B3811A] mx-5">
      <table class="w-full rounded-lg">
        <tbody>
          <tr>
            <td class="border-r border-r-gray-300 py-4 px-2">
              <div class="flex gap-x-2 items-center justify-around">
                <h2 class="text-blue text-xl font-semibold">{{ $t('your_rank') }}</h2>
                <div class="rounded-lg border border-blue text-center py-2 bg-white max-w-[200px]">
                  <p class="text-xl text-gray-900 font-semibold px-8">
                    {{ $rankFormat(currentUserData.current_rank) }}
                  </p>
                </div>
              </div>
            </td>
            <td class="border-r border-gray-300 px-2">
              <div class="flex flex-col justify-start">
                <p class="text-sm text-gray-dark font-medium">{{ $t('current_equity') }}</p>
                <p class="text-blue text-base font-medium">${{ currentUserData.equity }}</p>
              </div>
            </td>
            <td class="border-r border-gray-300 px-2">
              <div class="flex flex-col justify-start">
                <p class="text-sm text-gray-dark font-medium">{{ $t('current_balance') }}</p>
                <p class="text-blue text-base font-medium">${{ currentUserData.balance }}</p>
              </div>
            </td>
            <td class="border-r border-gray-300 px-2">
              <div class="flex flex-col justify-start">
                <p class="text-sm text-gray-dark font-medium">{{ $t('starting_balance') }}</p>
                <p class="text-blue text-base font-medium">$50</p>
              </div>
            </td>
            <td class="border-r border-gray-300 px-2">
              <div class="flex flex-col justify-start">
                <p class="text-sm text-gray-dark font-medium">{{ $t('return_percentage') }}</p>
                <p class="text-blue text-base font-medium">{{ currentUserData.percentage_gain }}%</p>
              </div>
            </td>
            <td class="border-r border-gray-300 px-2">
              <div class="flex flex-col justify-start">
                <p class="text-sm text-gray-dark font-medium">{{ $t('win_rate') }}</p>
                <p class="text-blue text-base font-medium">{{ currentUserData.win_rate }}%</p>
              </div>
            </td>
            <td class="px-2 text-center">
              <button @click="openGraph" class="bg-green text-white text-sm font-semibold px-8 rounded-lg py-2">
                {{ $t('view_graph') }}
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <!-- Your Rank -->
    <div v-if="userIsContestant"
      class="block lg:hidden border border-gray-300 rounded-lg py-3 mt-6 bg-[#20B3811A] mx-3">
      <div class="flex flex-col items-center px-3 pb-4 border-b border-b-gray-300">
        <h3 class="text-blue text-lg font-semibold mb-2">{{ $t('your_rank') }}</h3>
        <div class="rounded-lg border border-blue bg-white text-center py-2 w-full">
          <p class="text-xl text-gray-900 font-semibold text-center">
            {{ $rankFormat(currentUserData.current_rank) }}
          </p>
        </div>
      </div>
      <div class="px-3 flex justify-between py-3 border-b border-b-gray-300">
        <p class="text-sm text-gray-dark font-medium">{{ $t('current_equity') }}</p>
        <p class="text-blue text-base font-medium">${{ currentUserData.equity }}</p>
      </div>
      <div class="px-3 flex justify-between py-3 border-b border-b-gray-300">
        <p class="text-sm text-gray-dark font-medium">{{ $t('current_balance') }}</p>
        <p class="text-blue text-base font-medium">${{ currentUserData.balance }}</p>
      </div>
      <div class="px-3 flex justify-between py-3 border-b border-b-gray-300">
        <p class="text-sm text-gray-dark font-medium">{{ $t('starting_balance') }}</p>
        <p class="text-blue text-base font-medium">$50</p>
      </div>
      <div class="px-3 flex justify-between py-3 border-b border-b-gray-300">
        <p class="text-sm text-gray-dark font-medium">{{ $t('return_percentage') }}</p>
        <p class="text-blue text-base font-medium">{{ currentUserData.percentage_gain }}%</p>
      </div>
      <div class="px-3 flex justify-between py-3 border-b border-b-gray-300">
        <p class="text-sm text-gray-dark font-medium">{{ $t('win_rate') }}</p>
        <p class="text-blue text-base font-medium">{{ currentUserData.win_rate }}%</p>
      </div>
      <div class="text-center mt-6 mb-2">
        <button @click="openGraph" class="bg-green text-white text-sm font-semibold px-12 rounded-lg py-2">
          {{ $t('view_graph') }}
        </button>
      </div>
    </div>
    <!-- Contestants -->
    <div class="mx-5 grid grid-cols-1 lg:grid-cols-2 gap-x-4 mt-8">
      <!-- Contestants Table -->
      <div class="">
        <div class="mb-8">
          <div class="relative w-full">
            <input v-model="nameSearch" name="nameSearch"
              class="rounded-md bg-gray-100 ltr:pl-5 rtl:pr-5 w-full h-[50px] text-base text-gray-900 focus:outline-none focus:ring-0 focus:bg-gray-100 border placeholder:text-gray-900 placeholder:text-opacity-20 pr-12"
              type="text" :placeholder="$t('enter_full_name').tTitle()" @change="$emit('nameChange', nameSearch)" />
            <div class="p-3 absolute top-1/2 right-3 transform -translate-y-1/2 cursor-pointer"
              @click="$emit('nameChange', nameSearch)">
              <img :src="require('../../../assets/img/icons/competitions/search-icon.svg')" width="16" height="17"
                alt="Search Icon" />
            </div>
          </div>
        </div>
        <div class="w-full" v-if="!contestants">
          <h1 class="p-4 text-center text-base">{{ $t('no_records_to_show') }}</h1>
        </div>
        <div v-else id="contestantTable" class="overflow-auto select-none custom-scrollbar">
          <table class="w-full min-w-[652px]">
            <thead>
              <tr>
                <th class="text-blue font-bold text-base pb-4 ltr:text-left rtl:text-right">
                  {{ $t('rank').tTitle() }}
                </th>
                <th class="text-blue font-bold text-base pb-4 cursor-pointer ltr:text-left rtl:text-right">
                  {{ $t('full_name').tTitle() }}
                </th>
                <th class="text-blue font-bold text-base pb-4 cursor-pointer ltr:text-left rtl:text-right">
                  {{ $t('country').tTitle() }}
                </th>
                <th class="text-blue font-bold text-base pb-4 cursor-pointer ltr:text-left rtl:text-right">
                  {{ $t('active_days_nr').tTitle() }}
                </th>
                <th class="text-blue font-bold text-base pb-4 cursor-pointer ltr:text-left rtl:text-right">
                  {{ $t('percentage_gain').tTitle() }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="contestant in contestants" :key="contestant.account_number"
                @click="selectContestant(contestant)"
                :class="{ 'font-semibold': contestant.username == selectedContestant.username }"
                class="odd:bg-[#ebf0fc] cursor-pointer">
                <td
                  class="ltr:pl-7 rtl:pr-7 text-base text-gray-900 py-3 ltr:rounded-tl-md ltr:rounded-bl-md rtl:rounded-tr-md rtl:rounded-br-md">
                  {{ $rankFormat(contestant.current_rank) }}
                </td>
                <td class="text-base text-gray-900 py-3">{{ contestant.username }}</td>
                <td class="text-base text-gray-900 py-3">
                  <img v-if="getFlag(contestant.country_code) !== ''" class="rounded-full w-[30px] h-[30px]"
                    :src="getFlag(contestant.country_code)" alt="" />
                </td>
                <td class="text-base text-gray-900 py-3 tableCarousel">
                  {{ contestant.active_days }}
                </td>
                <td class="text-base text-gray-900 py-3 tableCarousel">{{ contestant.percentage_gain }}%</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="records > 0" class="flex justify-between w-full py-5 mx-5">
          <div class="w-[100px] h-[40px] font-body text-sm leading-10">{{ records }} {{ $t('records') }}</div>
          <div v-if="records > 10" class="flex h-[40px] font-body text-center leading-10">
            <pagination-component :page="page" :maxPerPage="10" :total="records" />
          </div>
          <div class="w-[100px]"></div>
        </div>
      </div>
      <!-- Contestants Stats -->
      <div>
        <div v-if="selectedContestant" class="grid grid-cols-3 gap-3">
          <div class="border border-gray-300 rounded-lg px-5 py-4">
            <h3 class="text-sm text-gray-dark font-medium">{{ $t('current_equity') }}</h3>
            <h2 class="text-blue text-base font-semibold">${{ selectedContestant.equity }}</h2>
          </div>
          <div class="border border-gray-300 rounded-lg px-5 py-4">
            <h3 class="text-sm text-gray-dark font-medium">{{ $t('current_balance') }}</h3>
            <h2 class="text-blue text-base font-semibold">${{ selectedContestant.balance }}</h2>
          </div>
          <div class="border border-gray-300 rounded-lg px-5 py-4">
            <h3 class="text-sm text-gray-dark font-medium">{{ $t('starting_balance') }}</h3>
            <h2 class="text-blue text-base font-semibold">$50</h2>
          </div>
          <div class="border border-gray-300 rounded-lg px-5 py-4">
            <h3 class="text-sm text-gray-dark font-medium">{{ $t('return_percentage') }}</h3>
            <h2 class="text-blue text-base font-semibold">{{ selectedContestant.percentage_gain }}%</h2>
          </div>
          <div class="border border-gray-300 rounded-lg px-5 py-4">
            <h3 class="text-sm text-gray-dark font-medium">{{ $t('highest_rank') }}</h3>
            <h2 class="text-blue text-base font-semibold">{{ selectedContestant.highest_rank }}</h2>
          </div>
          <div class="border border-gray-300 rounded-lg px-5 py-4">
            <h3 class="text-sm text-gray-dark font-medium">{{ $t('win_rate') }}</h3>
            <h2 class="text-blue text-base font-semibold">{{ selectedContestant.win_rate }}%</h2>
          </div>
        </div>
        <div v-if="selectedContestant" class="border border-gray-300 rounded-lg px-4 py-4 mt-3">
          <div class="flex w-full justify-between">
            <h3 class="text-lg text-blue font-semibold mr-2 break-words">{{ selectedContestant.username }}</h3>
            <div class="flex items-center gap-x-2">
              <div class="flex items-center gap-x-1">
                <div class="rounded-full w-4 h-4 bg-green"></div>
                <p class="text-sm text-gray-900">{{ $t('balance').tTitle() }}</p>
              </div>
              <div class="flex items-center gap-x-1">
                <div class="rounded-full w-4 h-4 bg-blue"></div>
                <p class="text-sm text-gray-900">{{ $t('equity').tTitle() }}</p>
              </div>
            </div>
          </div>
          <div v-if="Object.keys(graphOptions).length !== 0" width="100%">
            <apexchart type="area" :options="graphOptions.chartOptions" :series="graphOptions.series"> </apexchart>
          </div>
          <div class="w-full" v-else>
            <h1 class="p-4 text-center text-base">{{ $t('no_records_to_show') }}</h1>
          </div>
        </div>
      </div>
    </div>
    <Modal :opened="openUserGraph" @close="openUserGraph = false" :customClasses="'p-8'">
      <div>
        <div class="flex justify-between md:gap-8 gap-2 mb-2">
          <h3 class="text-lg text-blue font-semibold">{{ currentUserData.username }}</h3>
          <div class="flex items-center gap-x-2">
            <div class="flex items-center gap-x-1">
              <div class="rounded-full w-4 h-4 bg-green"></div>
              <p class="text-sm text-gray-900">{{ $t('balance').tTitle() }}</p>
            </div>
            <div class="flex items-center gap-x-1">
              <div class="rounded-full w-4 h-4 bg-blue"></div>
              <p class="text-sm text-gray-900">{{ $t('equity').tTitle() }}</p>
            </div>
          </div>
        </div>
        <div v-if="Object.keys(currentUserGraphOptions).length !== 0">
          <apexchart width="500px" type="area" :options="currentUserGraphOptions.chartOptions"
            :series="currentUserGraphOptions.series">
          </apexchart>
        </div>

        <div class="w-full" v-else>
          <h1 class="p-4 text-center text-base">{{ $t('no_records_to_show') }}</h1>
        </div>
      </div>
    </Modal>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import PaginationComponent from '../../../components/Pagination.vue'
import Modal from '@/components/reusableComponents/Modal.vue'
import { service } from '@/main'
import config from '@/config';

export default {
  props: {
    contestants: Array,
    userIsContestant: Boolean,
    page: Number,
    currentUserData: Object,
    selectedContestant: Object,
    records: Number,
  },
  data() {
    return {
      nameSearch: '',
      openUserGraph: false,
      graphOptions: {},
      currentUserGraphOptions: {},
    }
  },
  components: { PaginationComponent, Modal },
  created() {
    this.$bus.$on('pageChanged', p => {
      this.$emit('pageChange', p)
    })
  },
  mounted() {
    this.dragContainer('#contestantTable')
  },
  watch: {
    selectedContestant(cont) {
      if (cont && cont != undefined) {
        this.getContestantChart()
      }
    },
  },
  computed: {},
  methods: {
    async getContestantChart(user) {
      this['logoLoader/set'](true)
      user ? (this.currentUserGraphOptions = {}) : (this.graphOptions = {})
      try {
        const res = await service.post('/trading-competition/graph', {
          rank: user ? user.current_rank : this.selectedContestant.current_rank,
          competition_id: user ? user.competition_id : this.selectedContestant.competition_id,
        })
        if (res.data) {
          let data = res.data.data
          let equity = []
          let balance = []
          let xaxis = []
          if (data) {
            data.map(d => equity.push(d?.equity))
            data.map(d => balance.push(d?.balance))
            data.map(d => xaxis.push(d?.date))
            if (user) {
              this.currentUserGraphOptions = {
                series: [
                  {
                    name: this.$t('equity').tTitle(),
                    type: 'line', // Balance as a line
                    data: equity,
                    color: '#2B63E3',
                  },
                  {
                    name: this.$t('balance').tTitle(),
                    type: 'area', // Equity as an area
                    data: balance,
                    color: '#20B381',
                  },
                ],
                chartOptions: {
                  chart: {
                    height: 350,
                    type: 'line',
                  },
                  legend: {
                    show: false,
                  },
                  dataLabels: {
                    enabled: false,
                  },
                  stroke: {
                    curve: 'straight',
                    width: [3, 2],
                  },
                  fill: {
                    type: ['solid', 'gradient'],
                    opacity: [1, 1], // Full opacity for line, partial for area fill
                  },
                  xaxis: {
                    type: 'datetime',
                    categories: xaxis,
                  },
                  tooltip: {
                    x: {
                      format: 'dd/MMM/yy HH:mm',
                    },
                  },
                },
              }
            } else
              this.graphOptions = {
                series: [
                  {
                    name: this.$t('equity').tTitle(),
                    type: 'line', // Balance as a line
                    data: equity,
                    color: '#2B63E3',
                  },
                  {
                    name: this.$t('balance').tTitle(),
                    type: 'area', // Equity as an area
                    data: balance,
                    color: '#20B381',
                  },
                ],
                chartOptions: {
                  chart: {
                    height: 350,
                    type: 'line',
                  },
                  legend: {
                    show: false,
                  },
                  dataLabels: {
                    enabled: false,
                  },
                  stroke: {
                    curve: 'straight',
                    width: [3, 2],
                  },
                  fill: {
                    type: ['solid', 'gradient'],
                    opacity: [1, 1], // Full opacity for line, partial for area fill
                  },
                  xaxis: {
                    type: 'datetime',
                    categories: xaxis,
                  },
                  tooltip: {
                    x: {
                      format: 'dd/MMM/yy HH:mm',
                    },
                  },
                },
              }
          }
          this['logoLoader/set'](false)
        }
      } catch (error) {
        console.log(error)
        this['logoLoader/set'](false)
      }
    },
    getFlag(country) {
      if (!config.eligibleCountriesTradingCompetition.includes(country)) {
        return require(`../../../assets/img/icons/flags/${country.toLowerCase()}.png`)
      } else return ''
    },
    selectContestant(cont) {
      this.$emit('selectContestant', cont)
    },
    openGraph() {
      this.getContestantChart(this.currentUserData)
      this.openUserGraph = true
    },
    ...mapActions(['logoLoader/set']),
  },
}
</script>
